import { cn } from '@/lib/utils';
import { ElementType, forwardRef, ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';

interface IAppButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  hasIcon?: boolean;
  isIconOnly?: boolean;
  theme?: 'primary' | 'light' | 'light-outline' | 'default' | 'success' | 'error';
  icon?: ElementType;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  color?: string;
}

const themeStyles = {
  default: 'bg-coal-100 text-coal-700 hover:bg-coal-200 border-0',
  primary: 'bg-primary text-white hover:bg-primary-600 border-0 shadow-sm',
  success: 'bg-success text-white hover:bg-success-600 border-0 shadow-sm',
  error: 'bg-danger text-white hover:bg-danger-600 border-0 shadow-sm',
  light: 'bg-white text-coal-600 hover:bg-coal-100 border border-coal-200',
  'light-outline': 'bg-transparent text-coal-600 hover:bg-coal-100 border border-coal-200',
};

const sizeStyles = {
  xs: 'px-2.5 py-1.5 text-xs',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-2.5 text-base',
};

const iconOnlySizeStyles = {
  xs: 'h-7 w-7',
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
};

const AppButton = forwardRef<HTMLButtonElement, IAppButtonProps>(
  ({ isIconOnly, icon: Icon, children, theme = 'primary', size = 'md', loading, fullWidth, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        {...props}
        disabled={loading || props.disabled}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-lg font-medium capitalize transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:pointer-events-none disabled:opacity-50',
          isIconOnly ? iconOnlySizeStyles[size] : sizeStyles[size],
          isIconOnly
            ? 'bg-transparent text-coal-500 hover:bg-coal-100 hover:text-coal-700 border-0'
            : themeStyles[theme],
          fullWidth && 'w-full',
          className
        )}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {Icon && !isIconOnly && !loading && <Icon className="h-4 w-4" />}
        {isIconOnly && Icon ? <Icon className="h-4 w-4" /> : children}
      </button>
    );
  }
);

AppButton.displayName = 'AppButton';

export default AppButton;
